import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Spinner, Alert, OverlayTrigger, Popover,
} from 'react-bootstrap';

import AvisoFiltrosAlterados from 'components/Windx/AvisoFiltrosAlterados';
import FullscreenTable from 'components/Layout/MobileShell/FullscreenTable';

import { useGetPainelOptionsQuery, useLazyGetComparativoSMQuery } from 'services/api/windx';
import {
  CelulaEscala,
  ComparativoSMData,
  ComparativoSMRequest,
  ComparativoSMSemana,
  Modelo,
  OpcaoSelect,
  TipoRodada,
  Unidade,
  painelExibeTipoRodada,
} from 'helper/windx';
import {
  selectWindxIdsModelos,
  selectWindxIdSubdivisaoEspacial,
  selectWindxIdsEntidades,
  selectWindxPatamar,
  selectWindxUnidade,
  selectWindxTipoRodada,
  selectWindxTelaAtiva,
  selectWindxTriggerConsulta,
  selectWindxConsultaPendente,
} from 'redux/reducers/windxControlPanelReducer';
import { cssVar } from 'constants/theme';
import './index.scss';

// WINDX-060: nº de data_rodadas (linhas D-N) pedidas ao backend. Era 15, o que
// truncava a tabela na 15ª rodada (~D-16). A retenção SM do ETL é de 28 dias
// (RN-010) e o backend aceita até 30 (LIMITE_NUM_PREVISOES_MAX). Usamos o teto
// para "mostrar tudo que existe na janela": a tabela termina naturalmente onde
// o dado acaba (~D-27/D-28), sem pedir além do permitido pelo contrato.
const NUM_PREVISOES_DEFAULT = 30;

// WINDX-031: classe CSS por celula derivada de `escalas` (calculado no BE).
// Map sinal+intensidade -> classe. baseline/neutro nao tem cor.
function escalaParaClasse(escala: CelulaEscala | null | undefined): string {
  if (!escala) return '';
  if (escala.sinal === 'baseline' || escala.sinal === 'neutro') return '';
  const prefixo = escala.sinal === 'positivo' ? 'windx-cell-up' : 'windx-cell-down';
  return `${prefixo}-${escala.intensidade}`;
}

// WINDX-031: tooltip da celula. Mostra `+X,XX% vs <baseline>` apenas quando
// ha desvio mensuravel (intensidade > 0). baseline/neutro -> sem tooltip.
function formatarTooltipEscala(
  escala: CelulaEscala | null | undefined,
  labelBaseline: string,
): string | undefined {
  if (!escala) return undefined;
  if (escala.sinal === 'baseline' || escala.sinal === 'neutro') return undefined;
  const sinal = escala.delta_pct >= 0 ? '+' : '';
  return `${sinal}${escala.delta_pct.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}% vs ${labelBaseline}`;
}

function formatarValor(v: number | null | undefined, unidade: Unidade): string {
  if (v === null || v === undefined) return '-';
  if (unidade === 'fc') {
    // FC: value is 0..1 (NUMERIC(8,6) from DB), display as percentage.
    // WINDX-032 (PO 2026-05-22): limitar a 1 casa decimal — celulas eram
    // densas demais com 2-4 casas, prejudicando leitura comparativa.
    return `${(v * 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}%`;
  }
  // MW: standard format with thousands separator
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 3 });
}

// O backend envia ultima_atualizacao em ISO 8601 com offset fixo de Brasilia
// (-03:00, ADR-018). Preservamos a hora "como veio" extraindo os componentes
// diretamente da string — independente do TZ do navegador/Jest (TZ=UTC em CI).
function formatarUltimaAtualizacao(iso: string | undefined): string | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, yyyy, mm, dd, HH, MM] = m;
  return `${dd}/${mm}/${yyyy} às ${HH}h${MM}`;
}

// 'ONS_Oficial' -> 'ONS-Oficial'. Ultimo recurso do rotulo do modelo (ver
// `resolverLabelModelo`).
function formatarCodigoModelo(codigo: string | undefined): string {
  return (codigo ?? '').replace(/_/g, '-');
}

// WEOL-SM (#99): a coluna CEN e o "Modelo:" da toolbar mostram o label do
// modelo, nao mais o codigo ('CLIMATOLOGIA_MENSAL_SM' estourava a coluna fixa).
// Vale o label do painel-options, o mesmo da lista de Modelos do painel; sem o
// modelo no painel, o label que a resposta traz; sem nenhum, o codigo.
function resolverLabelModelo(
  modelo: ComparativoSMData['modelo'],
  modelosPainel: Modelo[] | undefined,
): string {
  return modelosPainel?.find((m) => m.id === modelo.id)?.label
    || modelo.label
    || formatarCodigoModelo(modelo.codigo);
}

// Rotulo curto do tipo de rodada, quando o painel-options nao traz os tipos
// (`tipos_rodada: null` com a flag do BE desligada: o painel assume 'final').
const ROTULO_TIPO_RODADA: Record<TipoRodada, string> = {
  preliminar: 'Preliminar',
  final: 'Final',
};

function resolverRotuloTipoRodada(
  tipo: TipoRodada,
  tiposRodada: OpcaoSelect<TipoRodada>[] | null | undefined,
): string {
  return tiposRodada?.find((t) => t.id === tipo)?.label ?? ROTULO_TIPO_RODADA[tipo];
}

// Ícone "pin" usado no botão de fixar/desfixar coluna de entidade.
// SVG inline (bootstrap-icons `pin-fill`) para evitar dependência externa
// — não há lib de ícones no projeto. A rotação para indicar "agarrado"
// é aplicada no CSS via classe `.is-ativo` (transform: rotate(-45deg)).
function IconePin() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className="windx-pin-icon"
      aria-hidden="true"
    >
      <path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A6 6 0 0 1 5 6.708V2.277a3 3 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354" />
    </svg>
  );
}

// Popover de ajuda do cabeçalho ("?"): explica como ler a colorização das
// células (escala de cores do ADR-026/WINDX-031). O texto é inline (não
// i18n) para manter o escopo da mudança em um único componente.
//
// Ordem do conteúdo (pedido do PO): (1) destaque de que a comparação é
// VERTICAL, contra a 1ª linha (referência); (2) o que cada cor significa;
// (3) que a intensidade reflete o tamanho da variação; (4) dica do tooltip
// por célula (% exata) — SÓ no desktop: a célula não tem handler de toque,
// então no mobile não existe forma de revelar a % exata (o `title` nativo
// não aparece em touch). Prometer "toque" aqui seria enganoso, entao a
// dica some no mobile em vez de trocar de texto.
function criarPopoverAjudaCores(modoMobile: boolean) {
  return (
    <Popover
      id="windx-comparativo-ajuda-popover"
      className="windx-comparativo__ajuda-popover"
    >
      <Popover.Header as="h3">Como ler as cores</Popover.Header>
      <Popover.Body>
        <p className="mb-2">
          Cada linha é comparada
          {' '}
          <strong>verticalmente com a 1ª linha</strong>
          {' '}
          (a previsão mais recente), na mesma coluna.
        </p>
        <ul className="windx-comparativo__ajuda-legenda">
          <li>
            <span className="windx-comparativo__ajuda-swatch windx-comparativo__ajuda-swatch--up" />
            <span>
              <strong>Verde</strong>
              : acima da referência
            </span>
          </li>
          <li>
            <span className="windx-comparativo__ajuda-swatch windx-comparativo__ajuda-swatch--down" />
            <span>
              <strong>Vermelho</strong>
              : abaixo da referência
            </span>
          </li>
        </ul>
        <p className={modoMobile ? 'mb-0' : 'mb-2'}>
          Quanto
          {' '}
          <strong>mais forte</strong>
          {' '}
          a cor,
          {' '}
          <strong>maior</strong>
          {' '}
          a diferença.
        </p>
        {!modoMobile && (
          <p className="windx-comparativo__ajuda-dica mb-0">
            💡 Passe o mouse em uma célula para ver a
            {' '}
            <strong>% exata</strong>
            .
          </p>
        )}
      </Popover.Body>
    </Popover>
  );
}

interface TabelaComparativoSMProps {
  /**
   * Mobile: mesma grade larga do desktop, com o mesmo pin de coluna
   * congelada — só ganha um botão de tela cheia (paisagem) a mais na
   * toolbar, já que a tabela é ampla demais para o retrato do celular.
   */
  modoMobile?: boolean;
}

export default function TabelaComparativoSM({
  modoMobile = false,
}: TabelaComparativoSMProps = {}) {
  const subdivisao = useSelector(selectWindxIdSubdivisaoEspacial);
  const idsModelos = useSelector(selectWindxIdsModelos);
  const idsEntidades = useSelector(selectWindxIdsEntidades);
  const patamar = useSelector(selectWindxPatamar);
  const unidade = useSelector(selectWindxUnidade);
  const tipoRodada = useSelector(selectWindxTipoRodada);
  const telaAtiva = useSelector(selectWindxTelaAtiva);
  const triggerConsulta = useSelector(selectWindxTriggerConsulta);
  const consultaPendente = useSelector(selectWindxConsultaPendente);

  // Estado local de UI: id da entidade fixada (coluna ancorada à esquerda
  // da área rolante, logo após CEN/PREV). `null` = nenhuma fixada.
  // Mantido em useState (escopo da view) porque é UI-only e não precisa
  // ser persistido nem compartilhado com outras telas.
  const [entidadeFixadaId, setEntidadeFixadaId] = useState<number | null>(null);
  // Mobile: tela cheia (paisagem) da tabela — substitui o pin de coluna
  // congelada, que não é renderizado nesse modo.
  const [telaCheia, setTelaCheia] = useState(false);

  // WEOL-SM (#99): rotulos do modelo e do tipo de rodada (labels do painel). E a
  // mesma query (mesmo argumento) que o painel de controle ja assina — o
  // Comparativo usa o painel-options do SM —, entao nao ha request extra.
  const { data: painelSM } = useGetPainelOptionsQuery('sm');

  // WEOL-SM (#99, contrato sm/comparativo v1.3): o modelo marcado no radio vai
  // direto ao BE — qualquer modelo ativo da tela, fim do `13 | 14`. O reducer
  // mantem o default [13] no Comparativo; o `?? 13` cobre a lista vazia com o
  // mesmo default do BE.
  const idModelo: number = idsModelos[0] ?? 13;

  const popoverAjudaCores = useMemo(
    () => criarPopoverAjudaCores(modoMobile),
    [modoMobile],
  );

  // WEOL-SM (#99): subdivisao + modelo + Tipo de Rodada. O botao e os
  // auto-disparos ja exigem o tipo (selectWindxFiltrosCompletos); este gate
  // local impede um trigger sem tipo de consultar sem `tipo_rodada`. Com a flag
  // do BE desligada, o useControlPanelRules assume 'final'. O modelo nao falta
  // hoje (o `?? 13` acima); fica no gate para a regra estar inteira aqui.
  const filtrosCompletos = (
    typeof subdivisao === 'number'
    && !!idModelo
    && !!tipoRodada
  );

  const payload = useMemo<ComparativoSMRequest | undefined>(() => {
    if (!filtrosCompletos) return undefined;
    return {
      id_modelo: idModelo,
      // WEOL-SM (#99): o BE filtra as 3 queries e a cache key por ele.
      tipo_rodada: tipoRodada as TipoRodada,
      patamar,
      num_previsoes: NUM_PREVISOES_DEFAULT,
      subdivisao_espacial: subdivisao as number,
      unidade,
      ...(idsEntidades.length > 0 ? { id_entidades: idsEntidades } : {}),
    };
  }, [filtrosCompletos, idModelo, tipoRodada, patamar, subdivisao, unidade, idsEntidades]);

  const [getComparativo, {
    data, isFetching, isError, error,
  }] = useLazyGetComparativoSMQuery();

  const lastTrigger = useRef(0);

  // Bug do PO 2026-05-18 #5: mesmo padrao do Bug 4 (commit 2868786a, fixado
  // em GraficoSH/GraficoSM) — o useEffect consumia `lastTrigger.current`
  // ANTES de validar `payload`. Quando `realizarConsulta()` chega com
  // filtros ainda nao consolidados (payload=undefined), o trigger era
  // "queimado" sem o request — o segundo clique manual precisava ser
  // estritamente maior para passar pela guarda.
  //
  // Bug do PO 2026-05-19 #A (pre-load): tabela aparecia com dados ao
  // entrar em Comparativo, mesmo sem o usuario ter clicado Realizar
  // Consulta. Causa: ao trocar de SM Semanal -> Comparativo, React monta
  // TabelaComparativoSM ANTES do useEffect do RouteWrappers/Windx
  // despachar `setTelaAtiva('comparativo')`. No primeiro render, o Redux
  // ainda contem filtros do Semanal (subdivisao=number, triggerConsulta
  // herdado >0). O payload eh valido, o useEffect roda e dispara
  // sm/comparativo/ com filtros do Semanal — antes do reset acontecer.
  //
  // Fix:
  //   1) gate `telaAtiva === 'comparativo'` — enquanto o Redux nao
  //      confirma que a tela atual e Comparativo, NENHUM request e
  //      disparado (protege contra leak no momento de mount).
  //   2) validar `payload` ANTES de consumir o trigger.
  //   3) `payload` nas deps — quando virar valido com trigger pendente,
  //      o effect re-roda e dispara automaticamente sem clique manual.
  useEffect(() => {
    // Defesa em profundidade (WINDX-bug-limpar-filtros, 2026-05-26): se o
    // counter regrediu (resetado para 0 por algum reducer), re-sincronizamos
    // o ref. Fix primario eh no reducer (preserva triggerConsulta no reset);
    // este gate protege contra regressao futura.
    if (triggerConsulta === 0) {
      lastTrigger.current = 0;
      return;
    }
    if (telaAtiva !== 'comparativo') return;
    if (triggerConsulta <= lastTrigger.current) return;
    if (!payload) return;
    lastTrigger.current = triggerConsulta;
    getComparativo(payload);
  }, [telaAtiva, triggerConsulta, payload, getComparativo]);

  // Reordena entidades quando há uma fixada — coloca-a primeiro, mantendo
  // a ordem relativa das demais. As colunas da entidade fixada recebem
  // position:sticky no CSS (left = CEN + PREV), formando o "freeze pane".
  const entidadesOrdenadas = useMemo(() => {
    if (!data) return [];
    if (entidadeFixadaId === null) return data.entidades;
    const idx = data.entidades.findIndex((e) => e.id === entidadeFixadaId);
    if (idx <= 0) return data.entidades;
    return [
      data.entidades[idx],
      ...data.entidades.filter((_, i) => i !== idx),
    ];
  }, [data, entidadeFixadaId]);

  // Limpa fixação se a entidade não estiver mais no dataset
  // (usuário trocou subdivisão ou alterou seleção).
  useEffect(() => {
    if (entidadeFixadaId === null) return;
    if (!data) return;
    if (!data.entidades.some((e) => e.id === entidadeFixadaId)) {
      setEntidadeFixadaId(null);
    }
  }, [data, entidadeFixadaId]);

  // WINDX-031: label da linha que serve de baseline para os tooltips. Backend
  // sempre marca a primeira previsao nao-null por coluna como baseline. No
  // caso comum (D-1 com todas as colunas preenchidas), e a primeira linha do
  // array. Usado apenas no texto do tooltip — o calculo de cor ja vem pronto.
  const labelBaseline = useMemo(() => {
    if (!data || data.previsoes.length === 0) return 'D-1';
    return data.previsoes[0].label;
  }, [data]);

  if (!filtrosCompletos) {
    // WEOL-SM (#99): o aviso so pede o Tipo de Rodada quando o painel do SM tem a
    // etapa (flag do BE ligada); sem ela o painel assume 'final'.
    const textoFiltrosIncompletos = painelExibeTipoRodada(painelSM, 'sm')
      ? 'Selecione subdivisão espacial e o Tipo de Rodada para gerar a tabela comparativa.'
      : 'Selecione subdivisão espacial para gerar a tabela comparativa.';
    return (
      <div className="mt-4 p-4 border rounded text-muted text-center">
        {textoFiltrosIncompletos}
      </div>
    );
  }

  if (consultaPendente) {
    return <AvisoFiltrosAlterados />;
  }

  if (isFetching) {
    return (
      <div className="mt-4 p-5 text-center" data-testid="windx-comparativo-loading">
        <Spinner animation="border" role="status" />
        <div className="mt-2 text-muted">Carregando comparativo…</div>
      </div>
    );
  }

  if (isError) {
    type ApiError = {
      status?: number;
      data?: { description?: string; message?: string; code?: number };
    };
    const apiError = error as ApiError | undefined;
    const apiData = apiError?.data;
    const httpStatus = typeof apiError?.status === 'number' ? apiError?.status : undefined;
    const ehSemDados = httpStatus === 404 || apiData?.code === 404;
    if (ehSemDados) {
      return (
        <Alert variant="info" className="mt-4" data-testid="windx-comparativo-empty">
          Nenhuma previsão semanal encontrada para o modelo e período selecionados.
        </Alert>
      );
    }
    const apiMessage = apiData?.description || apiData?.message
      || 'Falha ao carregar tabela comparativa.';
    return (
      <Alert variant="danger" className="mt-4" data-testid="windx-comparativo-error">
        {apiMessage}
      </Alert>
    );
  }

  if (!data || data.previsoes.length === 0) {
    return (
      <Alert variant="info" className="mt-4" data-testid="windx-comparativo-empty">
        Nenhuma previsão semanal encontrada para os filtros selecionados.
      </Alert>
    );
  }

  const ultimaAtualizacaoFormatada = formatarUltimaAtualizacao(data.ultima_atualizacao);
  // Do modelo DA RESPOSTA: descreve as linhas que estao na tela.
  const labelModelo = resolverLabelModelo(data.modelo, painelSM?.modelos);
  // Tipo efetivo que o BE ecoa; sem o eco (BE anterior ao #99), 'final'.
  const rotuloTipoRodada = resolverRotuloTipoRodada(
    data.tipo_rodada ?? 'final',
    painelSM?.tipos_rodada,
  );

  const togglePin = (id: number) => {
    setEntidadeFixadaId((prev) => (prev === id ? null : id));
  };

  const temFixada = entidadeFixadaId !== null;

  const toolbar = (
    <div className="windx-comparativo__toolbar">
      {ultimaAtualizacaoFormatada && (
        <span
          className="ultima-atualizacao"
          data-testid="windx-comparativo-ultima-atualizacao"
        >
          Última atualização:
          {' '}
          {ultimaAtualizacaoFormatada}
        </span>
      )}
      <div className="windx-comparativo__meta-linha">
        <span className="meta-tabela" data-testid="windx-comparativo-meta">
          Modelo:
          {' '}
          <strong>{labelModelo}</strong>
          {' • '}
          Rodada:
          {' '}
          <strong>{rotuloTipoRodada}</strong>
          {' • '}
          Patamar:
          {' '}
          <strong>{data.patamar_label}</strong>
          {' • '}
          Unidade:
          {' '}
          <strong>{data.unidade_label}</strong>
        </span>
        <OverlayTrigger
          placement="bottom-start"
          trigger={['hover', 'focus', 'click']}
          rootClose
          overlay={popoverAjudaCores}
        >
          <button
            type="button"
            className="windx-comparativo__ajuda-btn"
            aria-label="Como ler as cores da tabela"
            data-testid="windx-comparativo-ajuda"
          >
            ?
          </button>
        </OverlayTrigger>
        {modoMobile && (
          <button
            type="button"
            aria-label="Tela cheia"
            className="windx-comparativo__fullscreen-btn"
            onClick={() => setTelaCheia(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  const tabela = (
    <div className="windx-comparativo__container">
      <table className="windx-comparativo-table">
        <thead>
          <tr>
            <th
              rowSpan={2}
              className="col-cen text-center windx-sticky-col windx-sticky-col--cen"
            >
              CEN
            </th>
            <th
              rowSpan={2}
              className={`col-prev text-center windx-sticky-col windx-sticky-col--prev${temFixada ? '' : ' windx-sticky-col--ultimo'}`}
            >
              PREV
            </th>
            {entidadesOrdenadas.map((e, idxEntidade) => {
              const fixada = entidadeFixadaId === e.id;
              const headerClasses = [
                'subsistema-label',
                'text-center',
                idxEntidade > 0 ? 'subsistema-inicio' : '',
                fixada ? 'windx-sticky-col windx-sticky-col--entidade' : '',
              ].filter(Boolean).join(' ');
                // O <th> da entidade tem colSpan=N_semanas, ocupando a
                // posição inicial da faixa fixa. --col-i: 0.
              const headerStyle = fixada
                ? ({ '--col-i': 0 } as React.CSSProperties)
                : undefined;
              return (
                <th
                  key={`ent-${e.id}`}
                  colSpan={data.semanas.length}
                  className={headerClasses}
                  style={headerStyle}
                >
                  <span className="subsistema-label__conteudo">
                    <span className="subsistema-label__texto">{e.label}</span>
                    <button
                      type="button"
                      className={`windx-pin-button ${fixada ? 'is-ativo' : ''}`}
                      onClick={() => togglePin(e.id)}
                      aria-pressed={fixada}
                      aria-label={fixada ? `Desfixar coluna ${e.label}` : `Fixar coluna ${e.label}`}
                      title={fixada ? 'Desfixar coluna' : 'Fixar coluna'}
                      data-testid={`windx-comparativo-pin-${e.id}`}
                    >
                      <IconePin />
                    </button>
                  </span>
                </th>
              );
            })}
          </tr>
          <tr>
            {entidadesOrdenadas.flatMap((e, idxEntidade) => {
              const fixada = entidadeFixadaId === e.id;
              return data.semanas.map((s: ComparativoSMSemana, idxSemana) => {
                const ehUltimaSemanaFixada = fixada && idxSemana === data.semanas.length - 1;
                const semanaClasses = [
                  'semana-label',
                  idxEntidade > 0 && idxSemana === 0 ? 'subsistema-inicio' : '',
                  fixada ? 'windx-sticky-col windx-sticky-col--entidade' : '',
                  ehUltimaSemanaFixada ? 'windx-sticky-col--ultimo' : '',
                ].filter(Boolean).join(' ');
                  // CSS var --col-i indica o índice da semana dentro do
                  // subsistema fixado, usada no CSS para calcular o `left:`
                  // de cada coluna sticky individualmente.
                const stickyStyle = fixada
                  ? ({ '--col-i': idxSemana } as React.CSSProperties)
                  : undefined;
                return (
                  <th
                    key={`sem-${e.id}-${s.key}`}
                    className={semanaClasses}
                    title={`${s.data_inicio} a ${s.data_fim}`}
                    style={stickyStyle}
                  >
                    {s.label}
                  </th>
                );
              });
            })}
          </tr>
        </thead>
        <tbody>
          {data.previsoes.map((p) => (
            <tr key={p.data_rodada}>
              <td
                className="col-cen windx-sticky-col windx-sticky-col--cen"
                title={labelModelo}
              >
                <span className="col-cen__texto">{labelModelo}</span>
              </td>
              <td
                className={`col-prev windx-sticky-col windx-sticky-col--prev${temFixada ? '' : ' windx-sticky-col--ultimo'}`}
                title={`Data rodada: ${p.data_rodada}`}
              >
                {p.label}
              </td>
              {entidadesOrdenadas.flatMap((e, idxEntidade) => {
                const fixada = entidadeFixadaId === e.id;
                const valoresEntidade = p.valores[String(e.id)] ?? [];
                // WINDX-031: escalas vem do backend (contrato v1.2).
                // Optional chain para backcompat com responses antigos.
                const escalasEntidade = p.escalas?.[String(e.id)] ?? [];
                return data.semanas.map((s, idxSemana) => {
                  const valor = valoresEntidade[idxSemana];
                  const escala = escalasEntidade[idxSemana];
                  const classeEscala = escalaParaClasse(escala);
                  const tooltipEscala = formatarTooltipEscala(escala, labelBaseline);
                  const ehUltimaSemanaFixada = fixada && idxSemana === data.semanas.length - 1;
                  const tdClasses = [
                    'celula-dado',
                    idxEntidade > 0 && idxSemana === 0 ? 'subsistema-inicio' : '',
                    fixada ? 'windx-sticky-col windx-sticky-col--entidade' : '',
                    ehUltimaSemanaFixada ? 'windx-sticky-col--ultimo' : '',
                  ].filter(Boolean).join(' ');
                  const tdStyle = fixada
                    ? ({ '--col-i': idxSemana } as React.CSSProperties)
                    : undefined;
                  const ehVazio = valor === null || valor === undefined;
                  const valorRenderizado = formatarValor(valor, data.unidade);
                  return (
                    <td
                      key={`cell-${p.data_rodada}-${e.id}-${s.key}`}
                      data-testid={`windx-comparativo-cell-${p.data_rodada}-${e.id}-${s.key}`}
                      className={tdClasses}
                      title={tooltipEscala}
                      style={tdStyle}
                    >
                      {ehVazio ? (
                        <span className="windx-pill-empty">{valorRenderizado}</span>
                      ) : (
                        <span className={`windx-pill ${classeEscala}`.trim()}>
                          {valorRenderizado}
                        </span>
                      )}
                    </td>
                  );
                });
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="mt-4 windx-comparativo" data-testid="windx-comparativo-tabela">
      {toolbar}
      {!telaCheia && tabela}
      <FullscreenTable open={telaCheia} dismiss={() => setTelaCheia(false)}>
        {tabela}
      </FullscreenTable>
    </div>
  );
}
